import { hasRTSPPublication } from "../utils/streamOutputs";
import RTSPLinks from "./RTSPLinks";
import SeiStatus from "./SeiStatus";
import WhipStatus from "./WhipStatus";
import LlHlsLinks from "./LlHlsLinks";
import { Box, Tooltip, Typography } from "@mui/material";
import StreamState, { type StreamStateValue } from "./StreamState";
import SreamStateSpinner from "./SreamStateSpinner";
import type { StreamSummary } from "../types/streamSummary";
import type { FileSourceInfoSnapshot } from "../types/fileSourceInfo";
import {
  extractRecastOutputs,
  isRecastMode,
  resolveOutputState,
  type StreamOutputStatsSnapshot
} from "../utils/streamOutputs";
import { isStreamRunningState, shouldShowStreamActivitySpinner } from "../utils/streamState";

type StreamCardBriefProps = {
  stream: StreamSummary;
  fileSourceInfo?: FileSourceInfoSnapshot;
  streamOutputStats?: Map<string, StreamOutputStatsSnapshot>;
  getStreamState: (streamId: string) => StreamStateValue | undefined;
  getStreamStateMessage: (streamId: string) => unknown;
  getOutputState: (streamId: string, outputId: string) => StreamStateValue | undefined;
  getOutputStateMessage: (streamId: string, outputId: string) => string | null;
  hideOutputs?: boolean;
};

const StreamCardBrief = ({
  stream,
  fileSourceInfo,
  streamOutputStats,
  getStreamState,
  getStreamStateMessage,
  getOutputState,
  getOutputStateMessage,
  hideOutputs = false
}: StreamCardBriefProps) => {
  const streamState = getStreamState(stream.id);
  const streamStateMessage = getStreamStateMessage(stream.id);
  const streamHasError = streamState === "error";
  const trimmedErrorMessage = streamHasError && typeof streamStateMessage === "string"
    ? streamStateMessage.trim()
    : "";
  const errorCaption = streamHasError && trimmedErrorMessage ? `Error: ${trimmedErrorMessage}` : null;
  const streamStateTooltip = errorCaption ?? undefined;
  const showSpinner = shouldShowStreamActivitySpinner(streamState, streamStateMessage);
  const isStreamRunning = isStreamRunningState(streamState, streamStateMessage) || hasRTSPPublication(streamOutputStats);
  const recastOutputs = isRecastMode(stream.mode) ? extractRecastOutputs(stream.targets) : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <StreamState state={streamState} size={14} tooltip={streamStateTooltip} />
        <SreamStateSpinner
          streamHasError={streamHasError}
          showSpinner={showSpinner}
          errorCaption={errorCaption}
          tooltipPlacement="bottom"
          progress={fileSourceInfo?.progress}
          position={fileSourceInfo?.position}
          loopCount={fileSourceInfo?.loopCount}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {stream.sensor}
        </Typography>
      </Box>
      {!hideOutputs && recastOutputs.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
          {recastOutputs.map((output) => {
            const outputStats = streamOutputStats?.get(output.id);
            const rawOutputState = getOutputState(stream.id, output.id);
            const outputState = resolveOutputState(rawOutputState, outputStats, isStreamRunning, output.enabled);
            const outputError = outputState === "error"
              ? getOutputStateMessage(stream.id, output.id)
              : null;
            const outputTooltip = output.description ?? output.url ?? "";

            return (
              <Box
                key={output.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5
                }}
              >
                <StreamState
                  state={outputState}
                  size={8}
                  tooltip={outputError ? `Error: ${outputError}` : undefined}
                />
                <Tooltip title={outputTooltip} disableHoverListener={!outputTooltip}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {output.name}
                  </Typography>
                </Tooltip>
                <SeiStatus stats={outputStats} running={isStreamRunning && output.enabled !== false} />
                {output.type === "whip" && <WhipStatus stats={outputStats} />}
                      {output.type === "rtsp" && <RTSPLinks stats={outputStats} fallback={output.url} />}
                      {output.type === "llhls" && <LlHlsLinks stats={outputStats} fallback={output.url} />}
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};

export default StreamCardBrief;
